import { useCallback, useState } from 'react';
import html2canvas from 'html2canvas-pro';
import jsPDF from 'jspdf';
import sanitize from 'sanitize-filename';
import { FilenameService } from '@services/FilenameService';
import { getAnalytics } from '@services/analytics/AnalyticsService';
import { AnalyticsEvents } from '@services/analytics/AnalyticsEvents';
import { downloadBlob } from '@utils/downloadBlob';
import { handleRouteError } from '../../../../logger';
import type { OnePagerMode } from './EmissionsOnePagerPreview';

const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;

interface Period {
  start: string;
  end: string;
}

interface GenerateArgs {
  mode: OnePagerMode;
  node: HTMLElement;
  initiativeId: string;
  initiativeName: string;
  period: Period;
}

const buildImageFilename = (initiativeName: string, period: Period): string => {
  const slug = sanitize(`${initiativeName}_${period.start}_to_${period.end}_one-pager`).replace(/\s+/g, '-');
  return `${slug}.png`;
};

const pngDataUrlToBlob = (dataUrl: string): Blob => {
  const binary = atob(dataUrl.split(',')[1]);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new Blob([bytes], { type: 'image/png' });
};

const isCanvasTaintError = (err: unknown): boolean => {
  if (err instanceof DOMException && err.name === 'SecurityError') {
    return true;
  }
  return err instanceof Error && /tainted|cross-origin/i.test(err.message);
};

const humaniseError = (err: unknown): string => {
  if (isCanvasTaintError(err)) {
    return 'Your company logo couldn’t be embedded — it’s served from a host that doesn’t allow cross-origin image access. Re-upload it, or remove it from your profile and try again.';
  }
  if (err instanceof Error && err.message) {
    return err.message;
  }
  return 'Failed to export one-pager.';
};

const toError = (err: unknown): Error => (err instanceof Error ? err : new Error(String(err)));

const renderPdf = (canvas: HTMLCanvasElement, { initiativeName, period }: GenerateArgs): void => {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const heightMm = A4_WIDTH_MM * (canvas.height / canvas.width);
  pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, A4_WIDTH_MM, Math.min(heightMm, A4_HEIGHT_MM));
  pdf.save(
    FilenameService.forPDF({
      name: 'emissions-one-pager',
      subsidiary: initiativeName,
      dateRange: { startDate: new Date(period.start), endDate: new Date(period.end) },
    })
  );
};

const renderImage = (canvas: HTMLCanvasElement, { initiativeName, period }: GenerateArgs): void => {
  const blob = pngDataUrlToBlob(canvas.toDataURL('image/png'));
  downloadBlob(blob, buildImageFilename(initiativeName, period));
};

// Fires the export analytics event. Swallows (and logs) its own errors so a
// telemetry hiccup never surfaces as an export failure in the UI.
const trackExport = async ({ mode, initiativeId, period }: GenerateArgs): Promise<void> => {
  try {
    await getAnalytics().track(AnalyticsEvents.EmissionOnePagerExported, {
      initiativeId,
      format: mode,
      periodStart: period.start,
      periodEnd: period.end,
    });
  } catch (err) {
    handleRouteError(toError(err), { context: 'useGenerateOnePager.track' });
  }
};

export const useGenerateOnePager = () => {
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const clearError = useCallback(() => setError(undefined), []);

  const generate = useCallback(async (args: GenerateArgs) => {
    setError(undefined);
    setIsExporting(true);
    try {
      const canvas = await html2canvas(args.node, {
        useCORS: true,
        scale: 2,
        logging: false,
        backgroundColor: '#ffffff',
      });
      if (args.mode === 'pdf') {
        renderPdf(canvas, args);
      } else {
        renderImage(canvas, args);
      }
      await trackExport(args);
    } catch (err) {
      handleRouteError(toError(err), {
        context: 'useGenerateOnePager.generate',
        initiativeId: args.initiativeId,
        mode: args.mode,
        periodStart: args.period.start,
        periodEnd: args.period.end,
      });
      setError(humaniseError(err));
    } finally {
      setIsExporting(false);
    }
  }, []);

  return { generate, clearError, isExporting, error };
};
